/**
 * Equivalent Expressions (Properties) generator (6.PAFR.3.4).
 *
 * Multiple-choice questions about generating and recognizing equivalent
 * expressions using the distributive, commutative, and associative properties,
 * and combining like terms. Correct answers and distractors are built from Term
 * objects via termsToLatex, so the shown options match the underlying math and
 * the graded choice id is always one of the presented options.
 *
 * Families:
 *   F1: distributive expand — a(x + b) is equivalent to which expression?
 *   F2: identify the property used to rewrite an expression
 *   F3: combine like terms — ax + bx is equivalent to which expression?
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { varTerm, constant, termsToLatex, termsToText } from "../engine/expression";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "equivalent-expressions" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly coeffMax: number;
  readonly constMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F3"], coeffMax: 6, constMax: 8 },
  standard: { families: ["F1", "F2", "F3"], coeffMax: 9, constMax: 10 },
  hard: { families: ["F1", "F2", "F3"], coeffMax: 12, constMax: 12 },
  challenge: { families: ["F2", "F3"], coeffMax: 15, constMax: 15 },
};

const VARS = ["x", "y", "n", "a"] as const;

/**
 * Build latex-based multiple-choice options from a list of {latex, correct}
 * candidates: the first correct one is the answer; unique-by-latex, shuffled.
 */
function buildLatexChoices(
  rng: Rng,
  candidates: readonly { latex: string; correct: boolean }[],
): { choices: Choice[]; correctId: string } {
  const seen = new Set<string>();
  const kept: { latex: string; correct: boolean }[] = [];
  for (const c of candidates) {
    if (seen.has(c.latex)) continue;
    seen.add(c.latex);
    kept.push(c);
    if (kept.length >= 4) break;
  }
  const withIds = kept.map((c, i) => ({ id: `c${i}`, latex: c.latex, correct: c.correct }));
  const shuffled = rng.shuffle(withIds);
  const choices: Choice[] = shuffled.map((o) => ({ id: o.id, latex: o.latex }));
  const correct = shuffled.find((o) => o.correct);
  if (!correct) throw new Error(`${TOPIC}: no correct choice`);
  return { choices, correctId: correct.id };
}

export class EquivalentExpressionsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const v = rng.pick(VARS);
      const a = rat(rng.int(2, cfg.coeffMax));
      const b = rat(rng.int(2, cfg.constMax));

      let promptText: string;
      let promptLatex: string;
      let choices: Choice[];
      let correctId: string;
      let explanation: string;

      switch (family) {
        case "F1": {
          // a(v + b) == a*v + a*b (distributive)
          const correct = termsToLatex([varTerm(a, v), constant(a.mul(b))]);
          const source = `${a.toLatex()}(${v} + ${b.toLatex()})`;
          const candidates = [
            { latex: correct, correct: true },
            // Forgot to distribute to the constant.
            { latex: termsToLatex([varTerm(a, v), constant(b)]), correct: false },
            // Added instead of multiplied.
            { latex: termsToLatex([varTerm(a.add(rat(1)), v), constant(a.mul(b))]), correct: false },
            // Distributed only the constant.
            { latex: termsToLatex([varTerm(rat(1), v), constant(a.mul(b))]), correct: false },
          ];
          ({ choices, correctId } = buildLatexChoices(rng, candidates));
          promptText = `Which expression is equivalent to ${a.toString()}(${v} + ${b.toString()})?`;
          promptLatex = source;
          explanation = `Distribute ${a.toLatex()} to both terms: ${correct}.`;
          break;
        }
        case "F2": {
          // Identify which property rewrites a + b*v as b*v + a.
          const left = termsToText([constant(b), varTerm(a, v)]);
          const right = termsToText([varTerm(a, v), constant(b)]);
          const candidates = [
            { latex: "\\text{Commutative}", correct: true },
            { latex: "\\text{Associative}", correct: false },
            { latex: "\\text{Distributive}", correct: false },
            { latex: "\\text{Identity}", correct: false },
          ];
          ({ choices, correctId } = buildLatexChoices(rng, candidates));
          promptText = `Which property shows that ${left} = ${right}?`;
          promptLatex = `${termsToLatex([constant(b), varTerm(a, v)])} = ${termsToLatex([varTerm(a, v), constant(b)])}`;
          explanation = "Reordering terms of a sum uses the commutative property of addition.";
          break;
        }
        case "F3": {
          // a*v + b*v == (a+b)*v (combine like terms)
          const sum = a.add(b);
          const correct = termsToLatex([varTerm(sum, v)]);
          const candidates = [
            { latex: correct, correct: true },
            // Multiplied the coefficients.
            { latex: termsToLatex([varTerm(a.mul(b), v)]), correct: false },
            // Kept the variable squared style (wrong: adds an exponent).
            { latex: termsToLatex([varTerm(sum, v, 2)]), correct: false },
            // Off by one.
            { latex: termsToLatex([varTerm(sum.add(rat(1)), v)]), correct: false },
          ];
          ({ choices, correctId } = buildLatexChoices(rng, candidates));
          promptText = `Which expression is equivalent to ${a.toString()}${v} + ${b.toString()}${v}?`;
          promptLatex = termsToLatex([varTerm(a, v), varTerm(b, v)]);
          explanation = `Add the coefficients of like terms: ${sum.toLatex()}${v}.`;
          break;
        }
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [{ description: explanation, latex: promptLatex }],
        hints: [
          { level: 1, text: "Equivalent expressions have the same value for every value of the variable." },
          { level: 2, text: "Distribute a factor to every term; combine like terms by adding their coefficients." },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const equivalentExpressions = new EquivalentExpressionsGenerator();
